import { useContext, useCallback, useEffect, useRef, useState, useMemo } from "react"
import classNames from "classnames"
import { toUpper } from "lodash"
import { Box, Button, IconButton, Typography } from "@mui/material"
import { FirstPage, TuneOutlined, AutoMode } from "@mui/icons-material"
import Tooltip from "components/material/tooltip"

import { LOADING, ERROR, OK } from "helpers/data-statuses"
import { SelectedTypedInstrument } from "helpers/types/instruments/selected"
import useDebouncedCallback from "hooks/debounced-callback"
import { useOptimization } from "../../hooks/optimizer"
import { usePortfolioHoldings } from "hooks/rebalancer"
import { useAccountAlias, useIsDashboardOpLayoutEnabled } from "resources/account"
import { useForecasts } from "resources/forecasts"

import { Benchmark, Setup, Rules, Error, FactorRulesWrapper, UniverseSelector, Warning } from "./components"
import { getDefaultConfiguration, type FormSections } from "./configuration-sections"
import {
  calculateCurrentMarketValues,
  calculatePortfolioNetLong,
  getOptimizationResultName,
  getPortfolioType,
  transformConfigurationToForm,
  transformFormToConfiguration,
} from "./components/helpers/configuration"
import { useOptimizeForm, OptimizeFormContext } from "./form-context"
import { useTotalRisk } from "./hooks/use-total-risk"
import { useOptimizeBase } from "./hooks/use-optimize-base"
import { useScrollPosition } from "./hooks/use-scroll-position"
import { OptimizeExperimentsProps, BaseInstrumentWithName } from "./types"
import type { PositionSetInput } from "./components/factor-rules/types"
import { useIntradayMode } from "connected-components/dashboard/hooks/use-intraday-mode"

import "./styles.scss"

const SCROLL_POSITION_STORAGE_KEY = "optimizeExperiments.scrollPosition"

export const OPTIMIZATION_STRATEGY_LABEL = "Optimization Strategy"

export default function OptimizeExperiments({ context, modelMetadata }: OptimizeExperimentsProps) {
  const isDashboardOpLayoutEnabled = useIsDashboardOpLayoutEnabled()
  const {
    endDate,
    modelId,
    base: dashboardBase,
    isBaseOverridden,
    baseOverride,
    instrumentsMetadata,
    setIsBaseOverridden,
    setBaseOverride,
    setComparisonInstrument,
    setOptimizeConfiguration,
    setPreviewOptimizeConfiguration,
    previewOptimizeConfiguration,
    optimizeConfiguration,
    instrument,
    comparisonInstrument,
    isOptimizePanelExpanded: isPanelExpanded,
    setIsOptimizePanelExpanded: setIsPanelExpanded,
  } = useContext(context)
  const account = useAccountAlias()
  const [{ isIntradayMode }] = useIntradayMode()

  // Use custom hook to manage base instrument with proper weight calculation
  const { optimizerBase, optimizerBaseForQueries } = useOptimizeBase({
    modelId,
    endDate,
    isBaseOverridden,
    baseOverride,
    dashboardBase,
    instrumentsMetadata,
  })
  // Check if there's an inline position set (optimized result) as comparison
  const hasInlineComparison = comparisonInstrument && !comparisonInstrument.type
  const [totalRisk, totalRiskStatus] = useTotalRisk(instrument, modelId, endDate, optimizerBaseForQueries)
  const totalRiskValue = totalRisk?.values.totalRisk?.value
  const [forecasts, forecastsStatus] = useForecasts()
  const pendingForecastRef = useRef<boolean>(false)
  const [portfolioHoldings, portfolioHoldingsStatus] = usePortfolioHoldings(instrument, modelId, endDate)
  const portfolioMarketValues = useMemo(() => {
    if (portfolioHoldingsStatus !== OK) {
      return { longMarketValue: 0, shortMarketValue: 0 }
    }
    return calculateCurrentMarketValues(portfolioHoldings)
  }, [portfolioHoldings, portfolioHoldingsStatus])
  const portfolioNetLong = useMemo(() => {
    if (portfolioHoldingsStatus !== OK) {
      return 0
    }
    return calculatePortfolioNetLong(portfolioMarketValues)
  }, [portfolioMarketValues, portfolioHoldingsStatus])

  const portfolioType = useMemo(() => {
    return getPortfolioType(portfolioMarketValues)
  }, [portfolioMarketValues])

  const getInitialConfiguration = () => {
    const configuration = previewOptimizeConfiguration || optimizeConfiguration

    if (configuration) {
      try {
        return transformConfigurationToForm(configuration)
      } catch {
        return getDefaultConfiguration()
      }
    }

    return getDefaultConfiguration()
  }

  const [formError, setFormError] = useState<string | null>(null)
  const initialEndDate = useRef(endDate)

  // Live preview is disabled - optimization runs only on Apply button click
  const isLivePreviewEnabled = false

  // Scroll position tracking - hook handles saving and restoring automatically
  const { scrollRef: bodyRef } = useScrollPosition({
    storageKey: SCROLL_POSITION_STORAGE_KEY,
    isOpen: isPanelExpanded,
  })

  const form = useOptimizeForm({
    defaultValues: getInitialConfiguration(),
    onSubmit: async ({ value }: { value: FormSections }) => {
      if (canOptimize) {
        try {
          const currentConfiguration = transformFormToConfiguration(
            endDate,
            value.setup,
            value.rules,
            portfolioMarketValues,
            value.universe,
          )
          setFormError(null)
          handleRunOptimization(true)
          setOptimizeConfiguration(currentConfiguration)
          setPreviewOptimizeConfiguration(null)
        } catch {
          setFormError("Invalid form input. Please check your inputs and try again.")
        }
      }
    },
  })

  const [formValues, setFormValues] = useState(form.state.values)

  useEffect(() => {
    const unsubscribe = form.store.subscribe(() => {
      setFormValues(form.state.values)
    })
    return unsubscribe
  }, [form])

  const {
    runOptimization,
    optimizedPositionSet,
    optimizationStatus,
    optimizationError,
    getForecastData,
    forecastData,
    forecastDataStatus,
  } = useOptimization(
    instrument,
    modelId,
    endDate,
    optimizerBaseForQueries,
    formValues,
    portfolioMarketValues,
  )

  const disableForecasts = instrument?.type !== "PORTFOLIO"
  const hasForecastDataError = Boolean(
    !disableForecasts
    && forecastDataStatus === ERROR
    && formValues.setup.forecast
    && formValues.setup.forecast !== "none",
  )
  const isOptimizing = optimizationStatus === LOADING
  const hasError = hasForecastDataError || optimizationStatus === ERROR || !!optimizationError
  const canOptimize = Boolean(
    modelMetadata?.id
    && instrument?.id
    && !isOptimizing
    && modelId
    && endDate,
  )

  useEffect(() => {
    if (hasError) {
      // Error occurred, keep panel open
    } else if (optimizedPositionSet) {
      const positionSetWithName = {
        ...optimizedPositionSet,
        name: getOptimizationResultName(instrument?.type),
      }
      setComparisonInstrument(positionSetWithName)
    }
  }, [optimizedPositionSet, setComparisonInstrument, hasError, instrument?.type])

  const handleRunOptimization = useCallback((forceRun?: boolean) => {
    const selectedForecast = formValues.setup.forecast
    if (selectedForecast && selectedForecast !== "none" && !disableForecasts) {
      getForecastData()
      pendingForecastRef.current = true
      return
    }
    pendingForecastRef.current = false
    runOptimization(forceRun)
  }, [formValues.setup.forecast, getForecastData, runOptimization, disableForecasts])

  const forecastDataError = hasForecastDataError ? "Error fetching forecast data. Select a different forecast." : null

  useEffect(() => {
    if (pendingForecastRef.current && forecastData && forecastDataStatus === OK && !disableForecasts) {
      pendingForecastRef.current = false
      runOptimization(true)
    } else if (hasForecastDataError) {
      pendingForecastRef.current = false
    }
  }, [forecastData, forecastDataStatus, runOptimization, hasForecastDataError, disableForecasts])

  // Only run and save preview configuration when the panel is expanded (live preview/edit mode)
  // If there's a change, wait 3 seconds to see if there's another change
  // If there's no change, run the optimization
  const debouncedRunOptimization = useDebouncedCallback(() => {
    if (canOptimize && isPanelExpanded && form.state.canSubmit && isLivePreviewEnabled) {
      try {
        const values = form.state.values
        const currentConfiguration = transformFormToConfiguration(
          endDate,
          values.setup,
          values.rules,
          portfolioMarketValues,
          values.universe,
        )
        setFormError(null)
        handleRunOptimization()
        setPreviewOptimizeConfiguration(currentConfiguration)
      } catch {
        setFormError("Invalid form input. Please check your inputs and try again.")
      }
    }
  }, 3000)

  const [isDirty, setIsDirty] = useState(form.state.isDirty)

  useEffect(() => {
    const unsubscribe = form.store.subscribe(() => {
      setIsDirty(form.state.isDirty)
    })
    return unsubscribe
  }, [form])

  useEffect(() => {
    if (isDirty && isLivePreviewEnabled) {
      debouncedRunOptimization()
    }
  }, [formValues, isDirty, isLivePreviewEnabled, debouncedRunOptimization])

  useEffect(() => {
    const hasDateChanged = initialEndDate.current !== endDate
    if (hasDateChanged && isPanelExpanded && isLivePreviewEnabled) {
      debouncedRunOptimization()
    }
  }, [endDate, isPanelExpanded, isLivePreviewEnabled, debouncedRunOptimization])

  const handleSaveAndOptimize = useCallback(() => {
    form.handleSubmit()
  }, [form])

  // On panel close, we revert to last saved configuration by clearing preview and resetting form
  const handleClosePanel = useCallback(() => {
    setPreviewOptimizeConfiguration(null)

    const savedConfig = optimizeConfiguration
    if (savedConfig) {
      try {
        const formValues = transformConfigurationToForm(savedConfig)
        form.reset(formValues)
      } catch {
        form.reset(getDefaultConfiguration())
      }
    } else {
      form.reset(getDefaultConfiguration())
    }

    setIsPanelExpanded(false)
    setFormError(null)
  }, [setFormError, setPreviewOptimizeConfiguration, optimizeConfiguration, form, setIsPanelExpanded])

  const handleToggleDrawer = useCallback(() => {
    setIsPanelExpanded(!isPanelExpanded)
  }, [isPanelExpanded, setIsPanelExpanded])

  const handleBaseChange = useCallback((selectedInstrument: BaseInstrumentWithName | null) => {
    if (!selectedInstrument) {
      setBaseOverride(null)
      return
    }

    const { id, name, type } = selectedInstrument
    // Type guard because experimentId only exists on SelectedPortfolio/SelectedExperiment
    const experimentId = "experimentId" in selectedInstrument ? selectedInstrument.experimentId : undefined
    const baseType = toUpper(type) as SelectedTypedInstrument["type"]

    // Don't set weight here - it will be calculated based on metadata in optimizerBase
    const baseToSet: BaseInstrumentWithName = {
      type: baseType,
      id,
      name: name || id,
      ...(experimentId && { experimentId }),
    } as BaseInstrumentWithName

    setBaseOverride(baseToSet)
  }, [setBaseOverride])

  const handleBaseOverrideToggle = useCallback((checked: boolean) => {
    setIsBaseOverridden(checked)
    if (checked) {
      // When enabling override, initialize with dashboard base
      if (dashboardBase) {
        const name = instrumentsMetadata?.base?.name || dashboardBase.id
        setBaseOverride({ ...dashboardBase, name } as BaseInstrumentWithName)
      } else {
        setBaseOverride(null)
      }
    }
  }, [dashboardBase, instrumentsMetadata, setBaseOverride, setIsBaseOverridden])

  const canApply = !isOptimizing
    && canOptimize
    && form.state.canSubmit
    && !hasForecastDataError
    && (form.state.isDirty || !hasInlineComparison)
    && !isIntradayMode

  if (!isPanelExpanded) {
    return (
      <div className={classNames("optimize-experiments-content", "collapsed", { "use-dashboard-layout": isDashboardOpLayoutEnabled })}>
        <CollapsedVerticalTab
          onToggle={handleToggleDrawer}
        />
      </div>
    )
  }

  // Show expanded drawer
  return (
    <div className={classNames("optimize-experiments-content", "expanded", { "use-dashboard-layout": isDashboardOpLayoutEnabled })}>
      <div className="content">
        <div className="header-wrapper">
          <div className="header">
            <TuneOutlined color="primary" />
            <Typography variant="h6">
              <span>{OPTIMIZATION_STRATEGY_LABEL}</span>
            </Typography>
            <div className="header-actions">
              <Tooltip title="Collapse Optimization Panel">
                <IconButton onClick={handleToggleDrawer} size="medium" color="inherit">
                  <FirstPage />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <Typography variant="caption" color="textSecondary">
            Define portfolio rules and targets, then apply them to generate an optimized portfolio.
          </Typography>
        </div>
        <OptimizeFormContext.Provider value={form}>
          <Box className="body" ref={bodyRef}>
            <Box className="body-section">
              <Benchmark
                account={account}
                modelId={modelId}
                instrument={instrument}
                baseInstrument={optimizerBase ?? null}
                handleInstrumentSelected={handleBaseChange}
                isBaseOverridden={isBaseOverridden}
                onBaseOverrideToggle={handleBaseOverrideToggle}
              />

              <Setup
                currentTotalRiskValue={totalRiskValue}
                totalRiskStatus={totalRiskStatus}
                forecasts={disableForecasts ? [] : forecasts || []}
                forecastsStatus={forecastsStatus}
                hasBase={Boolean(optimizerBase)}
                disableForecasts={disableForecasts}
              />

            </Box>
            <Box className="body-section">
              <UniverseSelector modelId={modelId} />
            </Box>
            <Box className="body-section">
              <Rules portfolioType={portfolioType} portfolioNetLong={portfolioNetLong} />
            </Box>
            <Box className="body-section">
              <FactorRulesWrapper
                positionSet={instrument as PositionSetInput}
                basePositionSet={optimizerBaseForQueries}
                portfolioMarketValues={portfolioMarketValues}
              />
            </Box>
          </Box>
          <Box className="actions">
            <Button
              onClick={handleClosePanel}
              color="secondary"
              disabled={isOptimizing}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color={hasError ? "error" : "primary"}
              onClick={handleSaveAndOptimize}
              disabled={!canApply}
              startIcon={isOptimizing ? (
                <Box className="animated-loading-button-icon">
                  <AutoMode />
                </Box>
              ) : undefined}
            >
              {isOptimizing ? "Optimizing..." : hasError ? "Optimization Failed - Retry" : "Apply"}
            </Button>
          </Box>
          <Error error={formError || forecastDataError || optimizationError} />
          <Warning warning={isIntradayMode ? "Optimization is not available in intraday mode." : null} />
        </OptimizeFormContext.Provider>
      </div>
    </div>
  )
}

interface CollapsedVerticalTabProps {
  onToggle: () => void
}

function CollapsedVerticalTab({ onToggle }: CollapsedVerticalTabProps) {
  return (
    <Tooltip
      placement="right"
      title="Expand Optimizer Panel"
    >
      <div className="drawer-collapsed vertical-tab" onClick={onToggle}>
        <div className="vertical-content">
          <div className="expand-icon-container">
            <TuneOutlined className="expand-icon" />
          </div>
          <span className="vertical-text">{OPTIMIZATION_STRATEGY_LABEL}</span>
        </div>
      </div>
    </Tooltip>
  )
}
